import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isMobile } from 'react-device-detect';
import useIsUserLoggedIn from './useIsUserLoggedIn';
import { CategoriesReducer, CurrentUserReducer, RootReducer } from '../../models/Redux';
import { EventType, EventTypeName } from '../../models/enums/EventType';
import { getCurrentEvent } from '../../services/EventService';
import { EventData } from '../../models/EventData';
import { Language } from '../../models/Language';

let idleTimer: NodeJS.Timeout | null = null;
const refreshPeriod = 3 * 1000;

interface ReturnType {
    renderBuyButton: boolean;
    isUserLoggedIn: boolean;
    eventData: EventData;
    loading: boolean;
    purchased: boolean;
    upcoming: boolean;
    live: boolean;
    isCreator: boolean;
    eventType: { code: EventType | null; name: string | null };
    isInfluencer: boolean;
    openTicketing: boolean;
    firstLoad: boolean;
    hasAdvertisingEnabled: boolean;
}

const initialValuesEventData: EventData = {
    // @ts-ignore
    event: {},
    user: {},
    influencer: {},
    event_languages: [],
    event_category: undefined,
};

const useEvent = (id: string): ReturnType => {
    const isUserLoggedIn = useIsUserLoggedIn();

    const user = useSelector<RootReducer, CurrentUserReducer>(state => state.currentUser);
    const { categories } = useSelector<RootReducer, CategoriesReducer>(state => state.categories);
    const languages = useSelector<RootReducer, Language[]>(state => state.languages.languages);

    const [eventData, setEvent] = useState<EventData>(initialValuesEventData);
    const [loading, setLoading] = useState<boolean>(true);
    const [purchased, setPurchased] = useState<boolean>(false);
    const [upcoming, setUpcoming] = useState<boolean>(false);
    const [live, setLive] = useState<boolean>(false);
    const [openTicketing, setOpenTicketing] = useState<boolean>(false);
    const [isCreator, setIsCreator] = useState<boolean>(false);
    const [hasAdvertisingEnabled, setHasAdvertisingEnabled] = useState<boolean>(false);
    const [eventType, setEventType] = useState<{ code: number | null; name: string | null }>({
        code: null,
        name: null,
    });
    const [isInfluencer, setIsInfluencer] = useState<boolean>(false);
    const [firstLoad, setFirstLoad] = useState<boolean>(true); // flag for first load of event

    const [renderBuyButton, setRenderBuyButton] = useState<boolean>(false);

    const initEvent = useCallback(async () => {
        try {
            const event = await getCurrentEvent(id);
            event.event.event_category = categories.find(
                category => category.cate_ide === event.event.event_category_ide,
            );

            event.event.event_languages = languages.filter(
                language => JSON.parse(event.event.event_lang_ides).indexOf(language.lang_ide) > -1,
            );

            setEvent(event);
            setLoading(false);
            setOpenTicketing(event.event.event_opening_ticketing);
        } catch (err) {}
    }, [categories, id, languages]);

    const stopRefreshEventDataTimeout = () => {
        if (idleTimer) clearTimeout(idleTimer);
    };

    const checkIfPurchased = useCallback(() => {
        if (user.purchasedEvents) {
            user.purchasedEvents.forEach(event => {
                if (event.event_uuid === id) {
                    setPurchased(true);
                }
            });
        }
        if (user.isInfluencer) {
            setIsInfluencer(true);
        } else {
            setIsInfluencer(false);
        }
    }, [id, user.isInfluencer, user.purchasedEvents]);

    const checkEventState = useCallback(() => {
        if (eventData && eventData.event && eventData.event.event_ide !== undefined) {
            if (
                Object.keys(eventData.user).length > 0 &&
                user.user &&
                eventData.user.user_username === user.user.user_username
            ) {
                setIsCreator(true);
            }

            if (eventData.influencer && eventData.influencer.influencer_advertising) {
                setHasAdvertisingEnabled(true);
            }

            if (eventData.event && eventData.event.event_state === 0) {
                setUpcoming(true);
                setLive(false);
            }

            if (eventData.event && eventData.event.event_state === 1) {
                setLive(true);
                setUpcoming(false);
            }

            if (eventData.event && eventData.event.event_state === 2) {
                setLive(false);
                setUpcoming(false);
            }

            if (eventData.event) {
                if (eventData.event.ticket !== null) {
                    setEventType({
                        code: EventType.WEBRTC,
                        name: EventTypeName.WEBRTC,
                    });
                } else {
                    setEventType({
                        code: EventType.RTMP,
                        name: EventTypeName.RTMP,
                    });
                }
            }
        }
    }, [eventData, user.user]);

    const refreshEventDataTimeout = useCallback(() => {
        if (idleTimer) clearTimeout(idleTimer);

        idleTimer = setTimeout(async function () {
            try {
                const event = await getCurrentEvent(id);
                event.event.event_category = categories.find(
                    category => category.cate_ide === event.event.event_category_ide,
                );

                event.event.event_languages = languages.filter(
                    language => JSON.parse(event.event.event_lang_ides).indexOf(language.lang_ide) > -1,
                );

                setEvent(event);

                setOpenTicketing(event.event.event_opening_ticketing);
                checkIfPurchased();
                checkEventState();
                setFirstLoad(false);
                refreshEventDataTimeout();
            } catch (e) {
                refreshEventDataTimeout();
            }
        }, refreshPeriod);
    }, [categories, checkEventState, checkIfPurchased, id, languages]);

    useEffect(() => {
        if ((upcoming || (live && openTicketing)) && !purchased && !isCreator) {
            setRenderBuyButton(true);
        } else {
            setRenderBuyButton(false);
        }
    }, [upcoming, live, openTicketing, purchased, isCreator, firstLoad]);

    useEffect(() => {
        if (id && id !== '') initEvent();
    }, [categories, initEvent, id, languages]);

    useEffect(() => {
        if (!isMobile && id && id !== '') {
            refreshEventDataTimeout();
        }

        return () => {
            stopRefreshEventDataTimeout();
        };
    }, [id, refreshEventDataTimeout]);

    useEffect(() => {
        checkIfPurchased();
    }, [checkIfPurchased, user, id]);

    useEffect(() => {
        checkEventState();
    }, [checkEventState, eventData, user.user]);

    return {
        renderBuyButton,
        isUserLoggedIn,
        eventData,
        loading,
        purchased,
        upcoming,
        live,
        isCreator,
        eventType,
        isInfluencer,
        openTicketing,
        firstLoad,
        hasAdvertisingEnabled,
    };
};

export default useEvent;
